import type { FC } from 'react'
import { ChevronRight } from 'lucide-react'
import type { FleetHealthData } from '../core/dashboard-types'
import CardInfoButton from './CardInfoButton'

const healthMeta = {
  description: 'Overall fleet health score derived from PMS compliance, defect resolution, and survey currency.',
  endpoint: 'GET /pms/command-center-stats?companyId={cgaId}',
  logic: 'Percentage = (completed PMS jobs + closed defects + valid certificates) / total tracked items. Items grouped by health category (good, warning, critical) for quick assessment.',
}

type Props = {
  health: FleetHealthData
  onViewAll?: () => void
}

const dotColor: Record<FleetHealthData['items'][number]['color'], string> = {
  red: 'bg-red-500',
  amber: 'bg-amber-500',
  slate: 'bg-slate-300',
  emerald: 'bg-emerald-500',
}

const FleetHealthDonut: FC<Props> = ({ health, onViewAll }) => {
  return (
    <div className='card-hover bg-white rounded-2xl p-5 border border-slate-200 shadow-sm flex-1'>
      <div className='flex items-center justify-between mb-4'>
        <div className='flex items-center gap-2'>
          <h3 className='text-sm font-bold text-slate-900'>Fleet Health Summary</h3>
          <CardInfoButton meta={healthMeta} />
        </div>
        <button className='text-[11px] text-blue-600 hover:underline flex items-center gap-0.5' onClick={onViewAll}>
          View all <ChevronRight className='w-3 h-3' />
        </button>
      </div>
      <div className='flex items-center gap-5'>
        {/* SVG Donut */}
        <div className='relative w-24 h-24 flex-shrink-0'>
          <svg className='w-24 h-24 transform -rotate-90' viewBox='0 0 36 36'>
            <path
              d='M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831'
              fill='none'
              stroke='#E2E8F0'
              strokeWidth='3'
            />
            {health.percentage > 0 && (
              <path
                d='M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831'
                fill='none'
                stroke='#059669'
                strokeWidth='3'
                strokeDasharray={`${health.percentage}, 100`}
              />
            )}
          </svg>
          <div className='absolute inset-0 flex items-center justify-center'>
            <span className='text-lg font-bold text-slate-800'>{health.percentage}%</span>
          </div>
        </div>

        {/* Sidebreakdown */}
        <div className='flex-1 space-y-2'>
          {health.items.map((item, i) => (
            <div key={i} className='flex items-center justify-between'>
              <div className='flex items-center gap-2'>
                <span className={`w-2 h-2 rounded-full ${dotColor[item.color]}`} />
                <span className='text-[11px] text-slate-600'>{item.label}</span>
              </div>
              <span className='text-xs font-semibold text-slate-800'>{item.count}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export default FleetHealthDonut
